/**
 * Slice the CONTINGENCIES block out of stop-loss PDF markdown.
 * Keeps wording unchanged — only finds start/end headings and copies the span.
 */

/** Headings that start the contingencies block (matched against a cleaned line) */
export const CONTINGENCIES_START_PATTERNS = [
  /^contingencies\b/i,
  /^contingency\b/i,
  /^requirements\s+and\s+contingenc(?:y|ies)\b/i,
  /^stop[\s-]?loss\b.*\bcontingenc(?:y|ies)\b/i,
  /\bcontingenc(?:y|ies)\s*(?:&|and)\s*requirements\b/i,
  /\brequirements\s*(?:&|and)\s*contingenc(?:y|ies)\b/i,
  // Accurisk / ARS / Ryan Specialty packages
  /^proposal\s+contingenc(?:y|ies)\b/i,
  /^quote\s+contingenc(?:y|ies)\b/i,
  /\bproposal\s+contingenc(?:y|ies)\b/i,
  // Skyward packages: "PROPOSAL ASSUMPTIONS & CONTINGENCIES"
  /\bassumptions?\s*(?:&|and)\s*contingenc(?:y|ies)\b/i,
];

/**
 * Sibling / following sections that end the contingencies block
 * when they appear as a peer-or-higher heading after the start.
 */
export const CONTINGENCIES_END_SECTION_PATTERNS = [
  /^exclusions?\b/i,
  /^definitions?\b/i,
  /^lasering\b/i,
  /^disclosure/i,
  /^general\s+provisions?\b/i,
  /^limitations?\b/i,
  /^conditions?\b/i,
  /^coverages?\b/i,
  /^premium/i,
  /^rates?\b/i,
  /^quote\b/i,
  /^proposal\b/i,
  /^schedule\b/i,
  /^appendix\b/i,
  /^table\s+of\s+contents\b/i,
  /^important\s+notices?\b/i,
  /^notices?\b/i,
  /^signature/i,
  /\bsignature\s+page\b/i,
  /^acceptance\b/i,
  /^aggregat/i,
  /^specific\s+(?:deductible|stop)/i,
  /^attachment\s+point\b/i,
  /^contract\s+basis\b/i,
  /^run[\s-]?in\b/i,
  /^run[\s-]?out\b/i,
  /^claims?\s+funding\b/i,
  /^plan\s+provisions?\b/i,
];

/**
 * Strip markdown heading markers / numbering for pattern matching.
 * @param {string} line
 */
function cleanHeadingText(line) {
  return String(line || "")
    .replace(/^\s{0,3}#{1,6}\s+/, "")
    .replace(/^\s*\d+[.)]\s+/, "")
    .replace(/^\s*[IVXLCDM]+[.)]\s+/i, "")
    .replace(/^\s*[A-Z][.)]\s+/, "")
    .replace(/^[\s*_]+|[\s*_]+$/g, "")
    .replace(/\s+/g, " ")
    .trim();
}

/**
 * @param {string} line
 */
function isMarkdownHeading(line) {
  return /^\s{0,3}#{1,6}\s+\S/.test(line);
}

function markdownHeadingLevel(line) {
  const match = String(line || "").match(/^\s{0,3}(#{1,6})\s+\S/);
  return match ? match[1].length : 0;
}

/** Dot leaders in a table of contents, not the section itself. */
function isTableOfContentsLine(line) {
  return /\.{4,}/.test(String(line || ""));
}

/**
 * Short ALL-CAPS title line (common in stop-loss PDFs).
 * @param {string} line
 */
function isAllCapsTitle(line) {
  const t = String(line || "").trim();
  if (t.length < 3 || t.length > 80) return false;
  if (!/[A-Z]/.test(t)) return false;
  // Allow spaces, digits, punctuation between letters
  const letters = t.replace(/[^A-Za-z]/g, "");
  if (letters.length < 3) return false;
  return letters === letters.toUpperCase();
}

/**
 * @param {string} line
 */
function looksLikeHeadingLine(line) {
  const t = String(line || "").trim();
  if (!t) return false;
  if (isMarkdownHeading(t)) return true;
  if (isAllCapsTitle(t)) return true;
  // Numbered section titles: "3. Contingencies" / "IV. EXCLUSIONS"
  if (/^\d+[.)]\s+\S/.test(t) && t.length <= 80) return true;
  if (/^[IVXLCDM]+[.)]\s+\S/i.test(t) && t.length <= 80) return true;
  return false;
}

/** `## Page 12` markers inserted by the PDF parser — not real document sections */
function isParserPageHeading(line) {
  return /^#{1,3}\s*page\s+\d+\s*$/i.test(String(line || "").trim());
}

/**
 * anydoc often promotes body sentences to `##` (e.g. "This proposal is illustrative").
 * Those must not close the contingencies block.
 * @param {string} cleaned
 */
function isSentenceLikeHeading(cleaned) {
  const t = String(cleaned || "").trim();
  if (!t) return false;
  if (/[.!?]$/.test(t)) return true;
  const words = t.split(/\s+/).filter(Boolean);
  const lowercaseWords = words.filter((word) => /^[a-z]/.test(word));
  if (words.length >= 6 && lowercaseWords.length >= 2) return true;
  if (
    /^(this|the|these|those|a|an|if|when|please|note)\b/i.test(t) &&
    words.length >= 3
  ) {
    return true;
  }
  return false;
}

/**
 * @param {string} cleaned
 */
function isContingenciesStart(cleaned) {
  return CONTINGENCIES_START_PATTERNS.some((re) => re.test(cleaned));
}

/**
 * @param {string} cleaned
 */
function isContingenciesEndSection(cleaned) {
  // Never end on another contingencies-flavored heading
  if (isContingenciesStart(cleaned)) return false;
  return CONTINGENCIES_END_SECTION_PATTERNS.some((re) => re.test(cleaned));
}

/**
 * Extract the contingencies section from full stop-loss markdown/text.
 * @param {string} fullText
 * @returns {{ text: string, found: boolean, method: "heading" | "fallback" }}
 */
export function extractContingenciesSection(fullText) {
  const source = String(fullText ?? "");
  if (!source.trim()) {
    return { text: "", found: false, method: "fallback" };
  }

  const lines = source.split("\n");
  let startIdx = -1;

  for (let i = 0; i < lines.length; i += 1) {
    const raw = lines[i];
    const cleaned = cleanHeadingText(raw);
    if (!cleaned) continue;

    // Prefer clear heading lines; also allow a bare "CONTINGENCIES" body line
    if (isTableOfContentsLine(raw)) continue;
    const headingLike = looksLikeHeadingLine(raw) || cleaned.length <= 60;
    if (headingLike && isContingenciesStart(cleaned)) {
      startIdx = i;
      break;
    }
  }

  if (startIdx < 0) {
    return { text: source, found: false, method: "fallback" };
  }

  const startLevel = markdownHeadingLevel(lines[startIdx]) || 2;
  let endIdx = lines.length;
  for (let i = startIdx + 1; i < lines.length; i += 1) {
    const raw = lines[i];
    const cleaned = cleanHeadingText(raw);
    if (!cleaned) continue;
    if (isParserPageHeading(raw)) continue;
    if (!looksLikeHeadingLine(raw) && cleaned.length > 60) continue;

    const level = markdownHeadingLevel(raw);
    if (
      level &&
      level <= startLevel &&
      !isContingenciesStart(cleaned) &&
      !isSentenceLikeHeading(cleaned)
    ) {
      endIdx = i;
      break;
    }

    if (isContingenciesEndSection(cleaned) && looksLikeHeadingLine(raw) && (!level || level <= startLevel)) {
      endIdx = i;
      break;
    }

    // Next ALL-CAPS title that is a known sibling section
    if (isAllCapsTitle(raw) && isContingenciesEndSection(cleaned)) {
      endIdx = i;
      break;
    }
  }

  const sliced = lines.slice(startIdx, endIdx).join("\n").trim();
  if (!sliced) {
    return { text: source, found: false, method: "fallback" };
  }

  return { text: sliced, found: true, method: "heading" };
}
